import type { Account, DraftAccount } from '@interfaces/account';
import type { Affiliation } from '@interfaces/affiliation';

import { Icon } from '@ovComponents/1-primative/icon/icon';
import { Card, CardContent } from '@ovComponents/2-component/card/card';

import { AffiliationTable } from '@ovComponents/4-module/workflowCompletion/subSteps/account/affiliationTable';
import { GoalLinkingReview } from '@ovComponents/4-module/workflowCompletion/subSteps/account/goalLinking';
import { generateClientNameString } from '@utils/generateClientNameString';
import { Box, IconButton, Typography } from 'hephaestus/web';
import { CloseRounded as CloseRoundedIcon } from 'hephaestus/webIcons';
import { useTranslation } from 'react-i18next';
import { checkAffiliationMatch } from './corporateCash.dialogContent';

type CorporateCashReviewItemProps = {
  account: Account | DraftAccount;
  updateAffiliations: (affilations: Affiliation[]) => void;
  onDeleteAccount: () => void;
};
const hasAffiliates = (account: Account | DraftAccount) =>
  (account?.affiliations ?? []).length > 0;
const isDraft = (account: Account | DraftAccount) => !account?.id;

const DetailEntry = (props: any) => (
  <Box
    {...props}
    sx={[
      {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
      },
      ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
    ]}
  ></Box>
);

export const CorporateCashReviewItem = (
  props: CorporateCashReviewItemProps,
) => {
  const { t } = useTranslation([
    'workflowCompletions',
    'affiliationRelationship',
  ]);
  return (
    <Card variant='outlined' sx={{ mb: 2 }}>
      <CardContent>
        <DetailEntry sx={{ mb: 2 }}>
          <Typography variant='bodyLarge' sx={{ fontWeight: 600, mt: 1 }}>
            {t('workflowCompletions:accountReviewPanel.corporateCash')}
          </Typography>
          {props.account.id ? undefined : (
            <IconButton aria-label='close' onClick={props.onDeleteAccount}>
              <Icon icon={CloseRoundedIcon} size='small' />
            </IconButton>
          )}
        </DetailEntry>
        <AccountDetails
          account={props.account}
          updateAffiliations={props.updateAffiliations}
        />
      </CardContent>
    </Card>
  );
};

const AccountDetails = ({
  account,
  updateAffiliations,
}: {
  account: Account | DraftAccount;
  updateAffiliations: (affilations: Affiliation[]) => void;
}) => {
  const { t } = useTranslation([
    'affiliationRelationship',
    'shared',
    'workflowCompletions',
  ]);
  const handleDeleteAffiliation = (affiliation: Affiliation) => {
    if (!isDraft(account) || !account.affiliations) return;
    const newAffiliations = account.affiliations.filter(
      (x) => !checkAffiliationMatch(x, affiliation),
    );
    updateAffiliations(newAffiliations);
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        <DetailEntry>
          <Typography variant='labelLarge' sx={{ fontWeight: 600 }}>
            {t(
              'workflowCompletions:corporateCashAccountReivew.corporationName',
            )}
          </Typography>
          <Typography variant='bodyMedium' sx={{ fontWeight: 500 }}>
            {generateClientNameString(account?.user)}
          </Typography>
        </DetailEntry>
        <DetailEntry>
          <Typography variant='labelLarge' sx={{ fontWeight: 600 }}>
            {t('workflowCompletions:corporateCashAccountReivew.affiliates')}
          </Typography>
          <Typography variant='bodyMedium' sx={{ fontWeight: 500 }}>
            {hasAffiliates(account) ? t('shared:yes') : t('shared:no')}
          </Typography>
        </DetailEntry>
        {hasAffiliates(account) && (
          <DetailEntry>
            <AffiliationTable
              affiliations={account?.affiliations}
              showType={true}
              showAllocation={true}
              allowDelete={isDraft(account)}
              onDelete={handleDeleteAffiliation}
            />
          </DetailEntry>
        )}
        <DetailEntry>
          <GoalLinkingReview account={account} />
        </DetailEntry>
      </Box>
    </>
  );
};
